import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { ProductReviews, ProductReviewsForm, Button, BUTTON_TYPE } from '@bepure/tarkin';
import { NoLabelSelect } from 'components/NoLabelSelect';

declare const BASE_API_URL: string;

interface Review {
    score: number;
    title: string;
    description?: string;
    firstName?: string;
    lastName?: string;
    createdAt: Date;
    companyResponse?: string;
    verifiedCustomer?: boolean;
}

interface Props {
    reviewId?: number;
    reviews: {
        [sku: string]: {
            [id: number]: Review,
        },
    };
    tabSize?: number;
    productList: Array<{
        name: string,
        sku: string,
    }>;
}

const ProductReviewsSection = (props: Props) => {
    React.useLayoutEffect(() => {
        const element = props.reviewId ? document.getElementById('review-' + props.reviewId) : undefined;
        if (element) {
            element.scrollIntoView();
        }
    }, [props.reviewId]);
    const [overrideShowForm, setShowForm] = React.useState(false);
    const [currentProductSku, setCurrentProductSku] = React.useState(props.productList[0].sku);
    const [currentProductReviews, setCurrentProductReviews] = React.useState(props.reviews[props.productList[0].sku]);
    const productSelectRef = React.createRef<HTMLSelectElement>();

    const numReviews = Object.keys(props.reviews[currentProductSku]).length;
    let totalScore = 0;
    if (numReviews !== 0) {
        totalScore = Object.keys(currentProductReviews).map((key, index) => {
            return currentProductReviews[key].score;
        }).reduce((total, currentScore) => {
            return total + currentScore;
        });
    }

    const addReview = () => {
        return ((e: React.MouseEvent) => {
            setShowForm(!overrideShowForm);
        });
    };

    const changeProduct = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentProductSku(event.target.value);
        setCurrentProductReviews(props.reviews[event.target.value]);
    };

    const options = props.productList.filter(product => {
        return (product.sku && product.name && (Object.keys(props.reviews[product.sku]).length > 1));
    }).map(product => ({
        value: product.sku,
        text: product.name,
    }));

    return (
        <div
            style={{
                width: '100%',
            }}
        >
            <div className="product-reviews-section" id="product-reviews-section">
                {Object.keys(props.reviews).length > 1 ?
                    <div className="product-reviews-section__header-section">
                        <div className="product-reviews-section__header-section__input_wrapper">
                            <div className="product-reviews-section__header-section__input_wrapper__filter">
                                <NoLabelSelect
                                    ref={productSelectRef}
                                    selectProps={{
                                        onChange: changeProduct,
                                        value: currentProductSku,
                                        name: 'product-dropdown',
                                        id: 'product-dropdown',
                                        className: 'select__select',
                                    }}
                                    options={options}
                                />
                            </div>
                            <div className="product-reviews-section__header-section__input_wrapper__add-review">
                                <Button
                                    id={'product_reviews-add_a_review'}
                                    text={!overrideShowForm ? 'Leave A Review' : 'Close'}
                                    ariaLabel={'Leave A Review'}
                                    type={'button'}
                                    otherClasses={'product-reviews-section__header-section__input_wrapper__add-review__button'}
                                    onClick={addReview()}
                                    button_type={BUTTON_TYPE.PRIMARY}
                                />
                            </div>
                        </div>
                        <hr className="product-reviews-section__header-section__border" />
                    </div>
                    : null}
                <ProductReviewsForm
                    numReviews={numReviews}
                    averageScore={totalScore / numReviews}
                    submitForm={submitReview(currentProductSku)}
                    {...(props.productList.length > 1 ? { showForm: overrideShowForm } : undefined)}
                    hideButton={props.productList.length > 1}
                    {...(props.productList.length > 1 ? { formTitle: `${(props.productList.find(p => p.sku === currentProductSku) || { name: 'Customer' }).name} Reviews` } : undefined)}

                />
                <ProductReviews
                    reviewId={props.reviewId}
                    reviews={currentProductReviews}
                    tabSize={props.tabSize}
                />
            </div>
        </div>
    );
};

interface Form {
    firstName: string;
    lastName: string;
    email: string;
    score: number;
    title: string;
    description: string;
}
interface SubmitReviewResponse {
    error?: '';
}

const submitReview = (sku: string) => {
    return async (form: Form): Promise<SubmitReviewResponse> => {
        const response = await fetch(`${BASE_API_URL}/v1/product/submit-review`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                productSku: sku,
                review: form,
            }),
        });
        try {
            const content = await response.json();
            return Promise.resolve(content);
        } catch (error) {
            throw Promise.reject({
                error: error.message,
            });
        }
    };
};

interface ReviewResponse {
    reviews: {
        [sku: string]: {
            [id: number]: {
                description?: string;
                email?: string;
                firstName?: string;
                lastName?: string;
                productId?: number;
                productSku?: string;
                score: number;
                title: string;
                createdAt: Date;
                verifiedCustomer?: boolean;
            },
        },
    };
}

const getMultipleReviews = async (productSkus: string[]): Promise<ReviewResponse | null> => {
    const response = await fetch(`${BASE_API_URL}/v1/product/reviews/search`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            productSkus,
        }),
    });
    try {
        return await response.json();
    } catch (error) {
        throw Promise.reject(null);
    }
};

export default function render(reviewId: string) {
    const elements = document.getElementsByClassName('obiwan-product-reviews');
    Array.prototype.map.call(elements, (element: HTMLElement) => {
        const children = Array.prototype.slice.call(element.children);
        const productList = children.filter((child: HTMLInputElement) => child.dataset.product_name).map((child: HTMLInputElement) => {
            return {
                name: child.dataset.product_name || '',
                sku: child.value || '',
            }
        }, []);
        const productSkus = children.map((child: HTMLInputElement) => {
            return child.value;
        }, []);

        getMultipleReviews(productSkus).then((r) => {
            const reviews: {
                [sku: string]: {
                    [id: number]: Review,
                },
            } = r ? r.reviews : {};
            if (reviews) {
                Object.keys(reviews).map((sku) => {
                    reviews[sku] = Object.keys(reviews[sku]).map((id) => {
                        return {
                            ...reviews[sku][id],
                            createdAt: new Date(reviews[sku][id].createdAt),
                        };
                    }).sort((a: Review, b: Review) => b.createdAt.getTime() - a.createdAt.getTime());
                });
                return ReactDOM.render(
                    <ProductReviewsSection
                        reviewId={parseInt(reviewId, 10)}
                        reviews={reviews}
                        tabSize={4}
                        productList={productList}
                    />,
                    element,
                );
            }
        }).catch((r) => {
            // tslint:disable-next-line: no-console
            console.log('Product Reviews render failed: ' + r);
        });
    });
}
